import styled from "styled-components";
import { AutoSizer } from "react-virtualized";
import { FixedSizeList as List } from "react-window";
import { Icon } from "@iconify/react";
import FilterIcon from "@iconify-icons/fa/filter";
import * as ReactTable from "react-table";
import { useEffect, useRef, useState } from "react";
import StyledInput from "../shared/styled_input";
import useHideTableColumns, {
  TColumn
} from "../../hooks/use_hide_table_columns/use_hide_table_columns";
import CheckBoxUi from "../globalComponents/CheckBoxUi/CheckBoxUi";

interface IRenderRow<P extends object> {
  index: number;
  style: object;
  data: TColumn<P>[];
}

export interface IHideColumnProps<P extends object> {
  table: ReactTable.TableInstance<P>;
  columns: TColumn<P>[];
  title: string;
  headerTitleList?: string[];
  setIsActiveFilter?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ListContainer = styled.div`
  position: absolute;
  top: 26px;
  left: -80px;
  border: 1px solid ${props => props.theme.colors.green};
  border-radius: 4px;
  background-color: ${props => props.theme.colors.white};
  height: 140px;
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 180px;
  padding: 4px;
`;

const Container = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  position: relative;
  cursor: pointer;
  ${ListContainer} {
    visibility: ${props => (props.$isOpen ? "visible" : "hidden")};
  }
  z-index: 2;
  > span {
    font-weight: bold;
  }
`;

const ItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  label {
    font-weight: bold;
    min-width: max-content;
  }
`;

const IconContainer = styled.div`
  display: flex;
  color: ${props => props.theme.colors.green};
  border: 1px solid ${props => props.theme.colors.green};
  border-radius: 4px;
  justify-content: center;
  align-items: center;
  padding: 4px;
  &:hover {
    color: ${props => props.theme.colors.lightGreen};
    border: 1px solid ${props => props.theme.colors.lightGreen};
  }
`;

function Row<P extends object>(
  title: string,
  countItem: number,
  handleCountItem: (value: number) => void,
  headerTitleList?: string[],
  setIsActiveFilter?: React.Dispatch<React.SetStateAction<boolean>>
) {
  return ({ index, style, data }: IRenderRow<P>) => {
    let name = "";
    if (data[index].Header && typeof data[index].Header === "string") {
      name = data[index].Header as string;
    } else if (headerTitleList) {
      name = headerTitleList[index];
    }

    return (
      <ItemContainer key={index} style={style}>
        <CheckBoxUi
          checked={data[index].isVisible}
          onChange={() => {
            const isChecked = data[index].isVisible;
            if (setIsActiveFilter) {
              setIsActiveFilter(true);
            }
            if (isChecked && countItem > 1) {
              handleCountItem(countItem - 1);
              data[index].toggleHidden(true);
              const newData = [...data];
              newData[index].isVisible = !isChecked;
              localStorage.setItem(title, JSON.stringify(newData));
            } else if (!isChecked) {
              handleCountItem(countItem + 1);
              data[index].toggleHidden(false);
              const newData = [...data];
              newData[index].isVisible = !isChecked;
              localStorage.setItem(title, JSON.stringify(newData));
            }
          }}
          name={name}
        />
      </ItemContainer>
    );
  };
}

function HideColumn<P extends object>({
  columns,
  table,
  title,
  headerTitleList,
  setIsActiveFilter
}: IHideColumnProps<P>) {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { countItem, handleCountItem } = useHideTableColumns<P>({
    columns,
    table,
    title: `${title} for column`
  });

  useEffect(() => {
    const listRef = ref.current;
    if (isOpen) {
      listRef?.addEventListener("mouseleave", () => {
        setIsOpen(false);
      });
    }
    return () => {
      listRef?.removeEventListener("mouseleave", () => {
        setIsOpen(false);
      });
    };
  }, [isOpen]);

  return (
    <Container $isOpen={isOpen} onClick={() => setIsOpen(true)}>
      <span>{`필터`}</span>
      <IconContainer>
        <Icon icon={FilterIcon} />
      </IconContainer>
      <ListContainer ref={ref}>
        <AutoSizer>
          {({ height, width }) => {
            return (
              <List
                height={height}
                itemCount={columns.length}
                itemSize={30}
                width={width}
                itemData={columns}
              >
                {Row(
                  `${title} for column`,
                  countItem,
                  handleCountItem,
                  headerTitleList,
                  setIsActiveFilter
                )}
              </List>
            );
          }}
        </AutoSizer>
      </ListContainer>
    </Container>
  );
}

export default HideColumn;
